// reports/renderReportPdf.ts
//
// Renders a report template to a PDF on the user's own machine.
//
// How this replaced the old exporters
//
// There were two, and both were unreliable for the same underlying reason:
// they rendered inside the live application document.
//
//   • Hair and makeup rasterised the on-page sheet with html2canvas and pasted
//     the bitmap into jsPDF. That inherited every hazard of the host page — the
//     responsive `transform: scale()` wrapper corrupted text layout, webfonts
//     that had not finished loading produced fallback metrics, and Safari's
//     canvas side/area caps silently returned a blank bitmap on large sheets.
//     The output was also a photograph of text: no selectable glyphs, no vector
//     edges, several megabytes a page.
//
//   • Premium called window.print() on the whole application, so the result
//     depended on the app stylesheet, Tailwind's print behaviour and an icon
//     webfont all being in the right state.
//
// This module renders the backend's self-contained template inside an isolated
// same-origin iframe and prints *that*. The browser's own layout engine does
// the work, so the output is true vector PDF — selectable text, crisp at any
// zoom, a fraction of the size — and it is pixel-identical to the template
// because it is the template, laid out by a real engine rather than
// approximated by a canvas rasteriser. Nothing from the app can reach it: the
// iframe carries only the template's CSS, and every image is inlined as a data
// URL so there is no network fetch and no auth context to get wrong.
//
// The mobile app runs the same template through `Printing.convertHtml`, which is
// the same arrangement: the platform's WebView lays the document out and emits
// the PDF. One template, two real layout engines, zero backend work.

import { renderTemplate, type TemplateContext } from './mustache';
import { getReportTemplate, clearCachedTemplate, type ReportKind } from './templateClient';

/** Give the engine time to lay out and decode before printing. */
const LOAD_TIMEOUT_MS = 8_000;
const FONT_TIMEOUT_MS = 3_000;
/** Safari needs a paint tick between load and print or it captures mid-layout. */
const SETTLE_MS = 120;

export interface ExportOptions {
  /** Document title — the browser's default filename for the saved PDF. */
  title: string;
}

/**
 * Fetch the template for `kind` and interpolate it with `viewModel`.
 *
 * Separate from the export so the produced HTML can be asserted on in tests and
 * previewed in a browser without driving a print dialog.
 */
export async function renderReportHtml(kind: ReportKind, viewModel: TemplateContext): Promise<string> {
  const template = await getReportTemplate(kind);
  const html = renderTemplate(template.html, viewModel);
  if (!html.includes('</html>')) {
    // A truncated cache entry would print a half-sheet. Drop it so the next
    // attempt re-fetches rather than failing the same way forever.
    clearCachedTemplate(kind);
    throw new Error(`Report template "${kind}" rendered incomplete output.`);
  }
  return html;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => { setTimeout(resolve, ms); });
}

/** Resolve once the iframe document has loaded, or after a timeout. */
function waitForLoad(frame: HTMLIFrameElement): Promise<void> {
  return new Promise<void>(resolve => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    frame.addEventListener('load', finish, { once: true });
    setTimeout(finish, LOAD_TIMEOUT_MS);
  });
}

/**
 * Wait for the iframe's own fonts and images.
 *
 * Printing before a webfont resolves is what made the old exporter's typography
 * drift between runs; printing before the portrait decodes drops it from the
 * sheet entirely. Both are bounded — a slow asset degrades the sheet, it must
 * never hang the export.
 */
async function waitForAssets(doc: Document): Promise<void> {
  // `document.fonts` is absent in jsdom and older engines, so this is read
  // through a widened type rather than assumed present.
  const fontSet = (doc as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts;
  const fontsReady = fontSet?.ready;
  if (fontsReady) await Promise.race([fontsReady, delay(FONT_TIMEOUT_MS)]);

  const images = Array.from(doc.images).filter(img => !(img.complete && img.naturalWidth > 0));
  if (images.length > 0) {
    await Promise.race([
      Promise.all(images.map(img => new Promise<void>(resolve => {
        img.addEventListener('load', () => { resolve(); }, { once: true });
        img.addEventListener('error', () => { resolve(); }, { once: true });
      }))),
      delay(LOAD_TIMEOUT_MS),
    ]);
  }

  await delay(SETTLE_MS);
}

/**
 * Print a fully-rendered report document to PDF.
 *
 * The iframe is off-screen rather than `display:none`: a hidden subtree is not
 * laid out in several engines, and an unlaid-out document prints blank.
 */
export async function exportReportPdf(html: string, opts: ExportOptions): Promise<void> {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('title', opts.title);
  frame.style.position = 'fixed';
  frame.style.left = '-10000px';
  frame.style.top = '0';
  frame.style.width = '794px';
  frame.style.height = '1123px';
  frame.style.border = '0';
  frame.style.opacity = '0';

  document.body.appendChild(frame);

  try {
    // srcdoc keeps the frame same-origin, which is what lets us reach into it to
    // wait for assets and call print() — a blob: or data: URL would not.
    frame.srcdoc = html;
    await waitForLoad(frame);

    const win = frame.contentWindow;
    const doc = frame.contentDocument;
    if (!win || !doc) throw new Error('Report frame failed to initialise.');

    // The print UI shows the document title and uses it as the default filename.
    doc.title = opts.title;
    await waitForAssets(doc);

    win.focus();
    win.print();

    // The print dialog is modal but not awaitable. Hold the frame open long
    // enough for the engine to finish spooling; removing it too early prints a
    // blank or partial document.
    await delay(1_000);
  } finally {
    frame.remove();
  }
}

/** Fetch the template, interpolate, and print — the whole export in one call. */
export async function generateReportPdf(
  kind: ReportKind,
  viewModel: TemplateContext,
  opts: ExportOptions,
): Promise<void> {
  const html = await renderReportHtml(kind, viewModel);
  await exportReportPdf(html, opts);
}
